import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { cn } from "../lib/utils";

export type StoryEndSlideVariant = "prologue" | "epilogue";

const variantStyles: Record<
  StoryEndSlideVariant,
  { header: string; content: string; icon: string }
> = {
  prologue: {
    header:
      "bg-primary/10 border-b border-primary/20 text-foreground dark:bg-primary/15",
    content: "bg-primary/[0.04] dark:bg-primary/5",
    icon: "text-primary",
  },
  epilogue: {
    header:
      "bg-emerald-50/90 border-b border-emerald-200/70 text-foreground dark:bg-emerald-950/40 dark:border-emerald-800/60",
    content: "bg-emerald-50/40 dark:bg-emerald-950/25",
    icon: "text-emerald-700 dark:text-emerald-400",
  },
};

export interface StoryEndSlideCardProps {
  variant: StoryEndSlideVariant;
  icon: LucideIcon;
  /** Label + tooltip + optional edit icon — same role as activity pane title */
  titleSlot: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
}

/**
 * Shared Card shell for prologue/epilogue slideshow steps.
 * Mirrors the mobile + desktop layout split used in RenderReviewDialog activity cards.
 */
export function StoryEndSlideCard({
  variant,
  icon: Icon,
  titleSlot,
  subtitle,
  children,
}: StoryEndSlideCardProps) {
  const { i18n } = useTranslation();
  const vs = variantStyles[variant];

  const renderHeader = () => (
    <CardHeader
      className={cn(
        "flex flex-row flex-wrap items-center justify-center gap-2 py-3 px-4 md:px-6 flex-shrink-0",
        vs.header
      )}
    >
      <Icon className={cn("h-5 w-5 shrink-0", vs.icon)} aria-hidden />
      <div className="flex flex-col items-center min-w-0 flex-1 text-center">
        <CardTitle className="text-lg md:text-xl font-semibold leading-tight text-foreground">
          {titleSlot}
        </CardTitle>
        {subtitle ? (
          <p
            className={cn(
              "text-sm text-muted-foreground mt-1 max-w-prose",
              variant === "epilogue" && "dark:text-emerald-200/80"
            )}
          >
            {subtitle}
          </p>
        ) : null}
      </div>
    </CardHeader>
  );

  const renderMobileBody = () => (
    <CardContent
      className={cn("space-y-4 py-2 px-0 md:p-6 border-t-0", vs.content)}
    >
      {children}
    </CardContent>
  );

  const renderDesktopBody = () => (
    <CardContent
      className={cn(
        "flex-1 min-h-0 overflow-y-auto space-y-4 py-4",
        vs.content
      )}
    >
      {children}
    </CardContent>
  );

  return (
    <div className="relative w-full">
      {/* Mobile — matches RenderReviewDialog first Card */}
      <Card className="relative max-w-4xl mx-auto h-full md:h-auto flex flex-col md:rounded-md md:overflow-hidden md:hidden border-none md:border shadow-sm">
        {renderHeader()}
        {renderMobileBody()}
      </Card>

      {/* Desktop — matches RenderReviewDialog desktop block */}
      <div
        className="h-[70vh] flex-col hidden md:flex w-full"
        dir={i18n.dir()}
      >
        <main className="flex-1 min-h-0 flex flex-col items-center justify-between md:justify-center p-4 w-full">
          <Card className="relative max-w-4xl w-[30vw] mx-auto flex flex-col flex-1 min-h-0 md:rounded-md md:overflow-hidden shadow-sm">
            {renderHeader()}
            {renderDesktopBody()}
          </Card>
        </main>
      </div>
    </div>
  );
}
